import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Sparkles as SparklesIcon, FlaskConical, RefreshCw } from 'lucide-react';
import { Button, Pill } from '../';
import { useToast } from '../../../contexts/ToastContext';
import * as api from '../../../services/api';

/**
 * SkillEvalPanel — the ONE skill-eval surface.
 *
 * PROVE7-022 — extracted verbatim from the native LET eval that lived inline in
 * AgentDetailPageV2.jsx (the llmProviders memo + G248 defensive connector load,
 * the Generate picker with LLM-provider radio cards + model picker, and the
 * detail viewer: 5 KPI cards, phase-readiness banner, per-test "Why it failed"
 * + G249 quick-fix actions, AI recommendations, and run history). It replaces
 * the divergent ExteletPolicyEval so the ExteLet policy-skill eval is the SAME
 * component + logic + backend path (EvalService) the native agentlet uses — no
 * parallel implementation.
 *
 * Rendered INLINE inside the SkillPromotionCard eval slideout (per skill), so a
 * single component serves both surfaces. Native migration onto this component
 * is the tracked follow-up.
 *
 * @param {string}   props.agentId
 * @param {object}   props.skill              the policy/standard skill this panel evaluates
 * @param {Function} props.onChanged          reload the parent after generate/run/delete/quick-fix
 * @param {Function} [props.refreshGate]      optional phase-gate refresh (native parity)
 * @param {boolean}  [props.isRunbookManaged] AutoLet immutability (default false — ExteLets never are)
 */
export default function SkillEvalPanel({ agentId, skill, onChanged, refreshGate, isRunbookManaged = false }) {
  const toastCtx = useToast();
  const addToast = useCallback((a, t, d) => toastCtx?.addToast?.(a, t, d), [toastCtx]);

  /* ---- The judges this agent may use, as the platform names them ----
     This used to be derived here: the installed connectors crossed with the marketplace
     catalogue, filtered on connector status, with a platform row synthesised in front. That was
     a second list. The backend had its own, applying a different rule — it read whether the
     connector's TYPE declared an inference operation and never read status — and the two
     disagreed on a tenant holding an unusable LLM connector. Protect took the first row of the
     backend's list, measured with a connector that could not run, and the score fell back to a
     configuration check presented under that model's name.

     One owner (Principle 1, Principle 8): which connectors may judge is decided beside which
     connectors may be dispatched to, and both now read ConnectorStatus. This screen asks. */
  const [llmProviders, setLlmProviders] = useState([]);
  useEffect(() => {
    let live = true;
    if (!agentId) return undefined;
    api.getLlmProviders(agentId)
      .then((res) => {
        const rows = Array.isArray(res) ? res
          : Array.isArray(res?.data) ? res.data : [];
        if (live) setLlmProviders(rows);
      })
      .catch(() => { /* the panel still reports that no judge is available */ });
    return () => { live = false; };
  }, [agentId]);

  /* ---- state ---- */
  const [suite, setSuite] = useState(null);
  const [testCases, setTestCases] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  /* A position, not an id: the built-in judge carries a null connector id, which an id-keyed
     selection cannot tell apart from "nothing chosen". */
  const [pick, setPick] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [runningEval, setRunningEval] = useState(false);
  const [deletingEval, setDeletingEval] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const loadDetail = useCallback(async (s) => {
    setTestCases([]); setRuns([]);
    if (!s?.id) return;
    const [tcRes, runsRes] = await Promise.all([
      api.getEvalTestCases(agentId, s.id).catch(() => ({ ok: false })),
      api.getEvalRuns(agentId, s.id).catch(() => ({ ok: false })),
    ]);
    setTestCases(Array.isArray(tcRes?.data) ? tcRes.data : []);
    setRuns(Array.isArray(runsRes?.data) ? runsRes.data : []);
  }, [agentId]);

  // Newest non-archived suite for THIS skill (native openExistingEvalForSkill + openEvalDetail).
  const load = useCallback(async () => {
    if (!agentId || !skill?.id) return;
    setLoading(true);
    try {
      const res = await api.getEvals(agentId);
      const all = Array.isArray(res?.data) ? res.data
        : Array.isArray(res?.data?.content) ? res.data.content : [];
      const mine = all
        .filter((x) => x.skillId === skill.id && x.status !== 'archived')
        .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
      const s = mine[0] || null;
      setSuite(s);
      if (s) await loadDetail(s); else { setTestCases([]); setRuns([]); }
    } catch { setSuite(null); setTestCases([]); setRuns([]); }
    finally { setLoading(false); }
  }, [agentId, skill?.id, loadDetail]);

  useEffect(() => { load(); }, [load]);

  /* ---- handlers (logic verbatim from native handleGenerateSubmit / handleRunInDetail / handleDeleteInDetail) ---- */
  const handleGenerate = useCallback(async () => {
    if (!skill?.id) return;
    setGenerating(true);
    try {
      const payload = { skillId: skill.id };
      /* Nothing is sent for the built-in judge: the platform reads a missing connector id as its
         own and stamps its model. The model for a tenant judge is the one the platform chose —
         capability first, tier second — not one picked on this screen, because a manifest can
         mark an embeddings model `recommended` and a picker here would have offered it. */
      const judge = llmProviders[pick];
      if (judge?.id) {
        payload.llmProviderId = judge.id;
        if (judge.modelId) payload.llmModelId = judge.modelId;
      }
      const res = await api.generateEval(agentId, payload);
      const s = res?.data;
      if (!res?.ok || !s?.id) {
        addToast({ tone: 'danger', title: 'Generation failed',
          description: res?.error || res?.data?.error || 'Backend rejected the request.' });
        return;
      }
      addToast({ tone: 'success', title: `Generated ${s.totalTests || '?'} test case(s)`,
        description: 'Review the test cases below and click Run eval to execute.' });
      setSuite(s); await loadDetail(s);
      onChanged?.(); if (typeof refreshGate === 'function') await refreshGate();
    } catch (e) { addToast({ tone: 'danger', title: 'Generation threw', description: e?.message || String(e) }); }
    finally { setGenerating(false); }
  }, [agentId, skill, llmProviders, pick, addToast, loadDetail, onChanged, refreshGate]);

  const handleRun = useCallback(async () => {
    if (!suite?.id) return;
    setRunningEval(true);
    try {
      const res = await api.runEval(agentId, suite.id);
      const runData = res?.data;
      if (!res?.ok || runData == null) {
        addToast({ tone: 'danger', title: 'Run failed',
          description: res?.error || res?.data?.error || 'Backend rejected the run.' });
        return;
      }
      const pct = typeof runData.score === 'number' ? Math.round(runData.score * 100) : null;
      addToast({
        tone: (runData.passedTests > 0) ? 'success' : 'warning',
        title: `Run complete: ${runData.passedTests || 0}/${runData.totalTests || 0} passed${pct != null ? ` (${pct}%)` : ''}`,
        description: (runData.passedTests > 0)
          ? 'Skill evalScore + suite status updated. Gate should refresh.'
          : 'Every test failed — review the panel and adjust the skill config or re-generate.',
      });
      await loadDetail(suite);
      setSuite((prev) => prev ? {
        ...prev, status: 'completed', score: runData.score,
        passedTests: runData.passedTests, totalTests: runData.totalTests,
      } : prev);
      onChanged?.(); if (typeof refreshGate === 'function') await refreshGate();
    } catch (e) { addToast({ tone: 'danger', title: 'Run threw', description: e?.message || String(e) }); }
    finally { setRunningEval(false); }
  }, [agentId, suite, addToast, loadDetail, onChanged, refreshGate]);

  const handleDelete = useCallback(async () => {
    if (!suite?.id) return;
    setDeletingEval(true);
    try {
      const res = await api.deleteEvalSuite(agentId, suite.id);
      if (res?.ok === false) {
        addToast({ tone: 'danger', title: 'Delete failed',
          description: res?.error || res?.data?.error || `HTTP ${res?.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: 'Eval suite deleted',
        description: 'Generate a new one to re-evaluate this skill.' });
      setSuite(null); setDeleteConfirm(false); setTestCases([]); setRuns([]);
      setPick(0);
      onChanged?.(); if (typeof refreshGate === 'function') await refreshGate();
    } catch (e) { addToast({ tone: 'danger', title: 'Delete threw', description: e?.message || String(e) }); }
    finally { setDeletingEval(false); }
  }, [agentId, suite, addToast, onChanged, refreshGate]);

  /* ---- G249 quick-fix engine — verbatim from native computeQuickFix / handleQuickFix / handleAddGuardrail ---- */
  const computeQuickFix = useCallback((tc) => {
    if (tc.status !== 'failed' || !skill) return null;
    const scenario = tc.input?.scenario || '';
    switch (scenario) {
      case 'boundary':
        return { field: 'inferenceFallback', label: 'Inference fallback',
          current: skill.inferenceFallback || '(none)',
          options: ['default_response', 'queue', 'reject_gracefully'],
          desc: 'How the skill handles edge-case inputs when primary inference fails.' };
      case 'invalid_input':
      case 'injection':
      case 'guardrail_violation':
        return { field: 'guardrails', label: 'Guardrails',
          current: skill.guardrails ? JSON.stringify(skill.guardrails) : '(none)',
          addGuardrail: scenario === 'injection' ? 'inputSanitization'
            : scenario === 'invalid_input' ? 'inputValidation' : 'policyEnforcement',
          desc: scenario === 'injection' ? 'Add input sanitization to block SQL/XSS/prompt injection.'
            : scenario === 'invalid_input' ? 'Add input validation to reject malformed requests.'
            : 'Add guardrail policies to enforce safety boundaries.' };
      case 'unauthorized':
      case 'privilege_escalation':
        return { field: 'autonomy', label: 'Autonomy level',
          current: skill.autonomy || '(none)',
          options: ['HUMAN_CONTROLLED', 'SUPERVISED', 'ASSISTED'],
          desc: 'Reduce autonomy to require authorization checks.' };
      case 'performance':
        return { field: 'maxExecutionMs', label: 'SLA threshold (ms)',
          current: skill.maxExecutionMs || '(not set)', inputType: 'number', defaultValue: 5000,
          desc: 'Set the maximum acceptable execution time.' };
      case 'happy_path':
      case 'idempotency':
        return { field: 'description', label: 'Skill description',
          current: skill.description || '(empty)', inputType: 'text',
          desc: 'Add a meaningful description so the eval can validate behavior.' };
      default:
        return null;
    }
  }, [skill]);

  const handleQuickFix = useCallback(async (fieldName, value) => {
    if (!skill?.id) return;
    try {
      const res = await api.editSkill(agentId, skill.id, { [fieldName]: value });
      if (res?.ok) {
        addToast({ tone: 'success', title: `${fieldName} updated`, description: 'Re-run the eval to verify the fix lands.' });
        onChanged?.();
      } else {
        addToast({ tone: 'danger', title: 'Update failed', description: res?.error || res?.data?.error || 'Backend rejected the edit.' });
      }
    } catch (e) { addToast({ tone: 'danger', title: 'Update threw', description: e?.message || String(e) }); }
  }, [agentId, skill, addToast, onChanged]);

  const handleAddGuardrail = useCallback(async (guardrailType) => {
    if (!skill?.id) return;
    try {
      const existing = Array.isArray(skill.guardrails) ? skill.guardrails
        : skill.guardrails && typeof skill.guardrails === 'object' ? Object.keys(skill.guardrails) : [];
      const next = {};
      existing.forEach((g) => { next[typeof g === 'string' ? g : g.type || g] = true; });
      next[guardrailType] = true;
      const res = await api.editSkill(agentId, skill.id, { guardrails: next });
      if (res?.ok) {
        addToast({ tone: 'success', title: `Added "${guardrailType}" guardrail`, description: 'Re-run the eval to verify enforcement.' });
        onChanged?.();
      } else {
        addToast({ tone: 'danger', title: 'Guardrail update failed', description: res?.error || res?.data?.error || 'Backend rejected the edit.' });
      }
    } catch (e) { addToast({ tone: 'danger', title: 'Guardrail update threw', description: e?.message || String(e) }); }
  }, [agentId, skill, addToast, onChanged]);

  /* ---- render ---- */
  if (loading) return <p className="text-[12px] text-v2-ink-muted m-0">Loading…</p>;

  const skillIsInBuild = (skill?.skillPhase || 'build').toLowerCase() === 'build';

  /* === GENERATE PICKER (no suite yet) — verbatim body from native picker slideout === */
  if (!suite) {
    return (
      <div className="space-y-4 text-[12.5px]">
        <div className="flex items-center gap-2">
          <Button variant="primary" size="sm" onClick={handleGenerate} loading={generating}
            disabled={generating || !llmProviders.length}
            iconLeft={<FlaskConical size={13} />}>
            {generating ? 'Generating…' : 'Generate test cases'}
          </Button>
        </div>

        <div className="rounded-v2-md border border-v2-cyan bg-v2-cyan-soft px-3 py-2.5">
          <div className="font-semibold text-v2-cyan">Context-sensitive eval generation</div>
          <div className="text-v2-ink-secondary mt-0.5">
            The selected LLM analyses the skill's metadata, guardrails, and connector config to
            produce tailored test cases (functional, negative, security, guardrail, performance).
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-2">
            LLM provider
          </label>
          <div className="space-y-2">
            {llmProviders.map((p, i) => {
              const isSelected = pick === i;
              const builtIn = !p.id;
              return (
                <label key={p.id || 'platform'}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-v2-md cursor-pointer border transition-colors ${
                    isSelected ? 'border-v2-action bg-v2-action-soft' : 'border-v2-border hover:bg-v2-muted'}`}>
                  <input type="radio" name={`llmProvider-${skill.id}`} checked={isSelected}
                    onChange={() => setPick(i)} />
                  <div className="flex-1">
                    <div className="font-semibold text-v2-ink">{p.name}</div>
                    <div className="text-[11px] text-v2-ink-muted">
                      {builtIn ? `${p.modelId} · built-in`
                        : p.modelId ? `${p.modelId} · connected` : 'connected'}
                    </div>
                  </div>
                  <Pill tone={builtIn ? 'cyan' : 'success'} size="sm">
                    {builtIn ? 'BUILT-IN' : 'CONNECTED'}
                  </Pill>
                </label>
              );
            })}
          </div>
          {llmProviders.length <= 1 && (
            <div className="mt-2 rounded-v2-sm border border-v2-warning bg-v2-warning-soft px-3 py-2 text-[11px] text-v2-ink-secondary">
              No external LLM connectors connected. Add an Anthropic / OpenAI / Gemini / Azure
              connector on the Connectors page to use external models, or proceed with the platform default.
            </div>
          )}
        </div>

        {/* No model picker. Which of a provider's models can hold a conversation is a question
            InferenceModelResolver already answers for every caller, capability first and tier
            second; asking it again here is how an embeddings model marked `recommended` ends up
            being offered as a judge. The person picks a judge; the platform picks its model. */}

        <div className="rounded-v2-md border border-v2-border-subtle bg-v2-muted px-3 py-2.5 text-[11.5px] text-v2-ink-secondary leading-relaxed">
          <div className="font-semibold text-v2-ink mb-1">Generation will include:</div>
          <ul className="list-disc pl-4 space-y-0.5">
            <li>Functional tests (happy-path + boundary)</li>
            <li>Negative tests (invalid input + unauthorized access)</li>
            <li>Security tests (injection defence — high / critical risk only)</li>
            <li>Guardrail enforcement tests (if guardrails configured)</li>
            <li>Performance tests (SLA threshold compliance)</li>
            <li>Category-specific tests (based on skill type)</li>
          </ul>
        </div>
      </div>
    );
  }

  /* === DETAIL VIEWER (suite exists) — verbatim body from native detail slideout === */
  const raw = suite.score;
  const score01 = (raw == null) ? null : (Number(raw) <= 1 ? Number(raw) : Number(raw) / 100);
  const isCompleted = suite.status === 'completed';
  const ready = isCompleted && score01 != null && score01 >= 0.7;
  const banner = ready
    ? { title: 'Ready for phase promotion', tone: 'success',
        body: score01 >= 0.9
          ? `Score ${Math.round(score01 * 100)}% — meets the autonomous threshold (≥ 90%).`
          : `Score ${Math.round(score01 * 100)}% — passes minimum (70%) but below 90% for autonomous operation. Re-run after improvements.` }
    : score01 == null
      ? { title: 'Awaiting first run', tone: 'muted', body: 'Click Run eval below to execute every test case and produce a score.' }
      : { title: 'Not ready — improvements needed', tone: 'danger',
          body: `Score ${Math.round(score01 * 100)}% is below the 70% minimum. Review failed tests below and re-run.` };
  const bannerPalette = banner.tone === 'success'
    ? 'border-v2-success bg-v2-success-soft text-v2-success'
    : banner.tone === 'danger'
      ? 'border-v2-danger bg-v2-danger-soft text-v2-danger'
      : 'border-v2-border-subtle bg-v2-muted text-v2-ink-secondary';

  const pct = (suite.score != null && Number.isFinite(Number(suite.score)))
    ? `${Math.round(Number(suite.score) * (Number(suite.score) <= 1 ? 100 : 1))}%` : '—';
  const passed = suite.passedTests ?? 0;
  const total = suite.totalTests ?? testCases.length ?? 0;
  const failed = Math.max(0, total - passed);
  /* Four numbers, not five values. The fifth used to be the suite's lifecycle word, which at
     18px bold does not fit a fifth of a slideout and spilled into the card beside it — and
     which, sitting between a score and a pass count, read as a verdict. A suite completes
     whether or not a model ever judged it. The pill in the action row above already states the
     status, so this is a duplicate removed, not a fact lost. */
  const kpis = [
    { label: 'Score', value: pct },
    { label: 'Passed', value: String(passed) },
    { label: 'Failed', value: String(failed) },
    { label: 'Total', value: String(total) },
  ];

  /* The newest run is the one the score on screen came from; runs arrive newest first. */
  const latestRun = runs[0] || null;
  const judgedNote = latestRun?.evaluationMethod === 'config_based'
    ? 'no model judged this run — scored by configuration check'
    : latestRun?.evaluationMethod === 'mixed'
      ? 'only some of this run was judged by a model'
      : null;

  const latestRunWithRecs = runs.find((r) => r.recommendations);
  const recs = latestRunWithRecs?.recommendations;
  const recItems = Array.isArray(recs?.items) ? recs.items : Array.isArray(recs) ? recs : [];
  const recTone = (pri) => {
    switch (String(pri || '').toLowerCase()) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'cyan';
      default: return 'muted';
    }
  };

  return (
    <div className="space-y-4 text-[12.5px]">
      {/* action row */}
      <div className="flex items-center gap-2 flex-wrap">
        <Pill tone={suite.status === 'completed' ? 'success' : suite.status === 'failed' ? 'danger' : suite.status === 'archived' ? 'muted' : 'cyan'} size="sm">
          {suite.status || 'created'}
        </Pill>
        {suite.llmProvider && (
          <span className="text-[10.5px] text-v2-ink-muted">{suite.llmProvider}{suite.llmModel ? ` · ${suite.llmModel}` : ''}</span>
        )}
        {/* The judge named above is what the suite ASKED for. Whether it answered is a different
            fact, and the run is the only thing that knows it: when the judge cannot run, the
            evaluation loop scores configuration completeness instead and still produces a
            number. Printed live as "AWS Bedrock · claude-3-5-sonnet — 57%" for a run no model
            took part in, with the score the promotion gate reads. Silence here means the run did
            not record how it was scored, which is not the same as saying no model judged it. */}
        {judgedNote && (
          <span className="text-[10.5px] font-semibold text-v2-warning">{judgedNote}</span>
        )}
        <div className="flex-1" />
        {suite.status !== 'archived' && (
          <Button variant="primary" size="sm" onClick={handleRun} loading={runningEval}
            disabled={runningEval || deletingEval || !skillIsInBuild}
            title={!skillIsInBuild
              ? `Skill is in ${skill?.skillPhase} phase. Regress to Build to re-run the eval.`
              : (suite.status === 'completed' ? 'Re-run the eval suite — overwrites previous score' : 'Run the eval suite — populates score + fail/pass breakdown')}>
            {runningEval ? 'Running…' : (suite.status === 'completed' ? 'Re-run eval' : 'Run eval')}
          </Button>
        )}
        {!deleteConfirm && (
          <Button variant="ghost" size="sm" className="!text-v2-danger hover:!bg-v2-danger-soft"
            onClick={() => setDeleteConfirm(true)} disabled={runningEval || deletingEval}>
            Delete suite
          </Button>
        )}
        {deleteConfirm && (
          <>
            <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(false)} disabled={deletingEval}>Cancel</Button>
            <Button variant="danger" size="sm" onClick={handleDelete} loading={deletingEval}>
              {deletingEval ? 'Deleting…' : 'Confirm delete'}
            </Button>
          </>
        )}
      </div>

      {/* the measurements */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {kpis.map((c) => (
          <div key={c.label} className="rounded-v2-md border border-v2-border-subtle bg-v2-surface px-3 py-2 text-center">
            <div className="text-[9px] uppercase tracking-wider text-v2-ink-muted">{c.label}</div>
            <div className="text-[18px] font-bold text-v2-ink mt-0.5">{c.value}</div>
          </div>
        ))}
      </div>

      {/* readiness banner */}
      <div className={`rounded-v2-md border px-3 py-2.5 ${bannerPalette}`}>
        <div className="font-semibold">{banner.title}</div>
        <div className="text-[11.5px] text-v2-ink-secondary mt-0.5">{banner.body}</div>
      </div>

      {/* test cases */}
      <div>
        <div className="text-[13px] font-semibold text-v2-ink mb-2 flex items-center gap-2">
          <FlaskConical size={14} /> Test cases ({testCases.length})
        </div>
        {testCases.length === 0 ? (
          <div className="rounded-v2-md border border-v2-border-subtle bg-v2-muted px-3 py-3 text-v2-ink-muted text-[11.5px] italic">
            No test cases on this suite yet. If the suite was just generated, refresh after a few seconds.
          </div>
        ) : (
          <div className="space-y-2">
            {testCases.map((tc, idx) => {
              const actual = tc.actualOutput || {};
              const status = tc.status || 'pending';
              const palette = status === 'passed' ? 'border-v2-success bg-v2-success-soft'
                : status === 'failed' ? 'border-v2-danger bg-v2-danger-soft'
                : 'border-v2-border-subtle bg-v2-muted';
              const quickFix = computeQuickFix(tc);
              const qfInputId = `qf-${tc.id || idx}-${quickFix?.field || ''}`;
              return (
                <div key={tc.id || idx} className={`rounded-v2-md border px-3 py-2 ${palette}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Pill tone={status === 'passed' ? 'success' : status === 'failed' ? 'danger' : 'muted'} size="sm">{status}</Pill>
                    {tc.testType && <Pill tone="muted" size="sm">{tc.testType}</Pill>}
                    {tc.severity && <span className="text-[10px] text-v2-ink-muted">severity: {tc.severity}</span>}
                  </div>
                  <div className="font-semibold text-v2-ink">{tc.description || `Test #${idx + 1}`}</div>
                  {status === 'failed' && actual.reason && (
                    <div className="mt-1 text-[11.5px] text-v2-danger"><strong>Why it failed:</strong> {actual.reason}</div>
                  )}
                  {status === 'failed' && actual.suggestedFix && (
                    <div className="mt-1 text-[11.5px] text-v2-warning"><strong>Fix:</strong> {actual.suggestedFix}</div>
                  )}

                  {quickFix && !isRunbookManaged && skillIsInBuild && (
                    <div className="mt-2 rounded-v2-sm border border-v2-action-soft bg-v2-surface px-3 py-2">
                      <div className="text-[10.5px] text-v2-ink-secondary mb-1">
                        <strong className="text-v2-action-strong">Quick fix:</strong> {quickFix.desc}
                      </div>
                      <div className="text-[10px] text-v2-ink-muted mb-2">
                        Field <code className="px-1 py-0.5 rounded-v2-sm bg-v2-muted">{quickFix.label}</code>
                        {' '}· current{' '}
                        <code className="px-1 py-0.5 rounded-v2-sm bg-v2-danger-soft text-v2-danger">
                          {typeof quickFix.current === 'string' && quickFix.current.length > 60 ? quickFix.current.slice(0, 60) + '…' : String(quickFix.current)}
                        </code>
                      </div>
                      {quickFix.options && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {quickFix.options.map((opt) => (
                            <Button key={opt} variant="secondary" size="sm" onClick={() => handleQuickFix(quickFix.field, opt)}>
                              Set to "{opt.replace(/_/g, ' ')}"
                            </Button>
                          ))}
                        </div>
                      )}
                      {quickFix.addGuardrail && (
                        <Button variant="secondary" size="sm" iconLeft={<Plus size={11} />} onClick={() => handleAddGuardrail(quickFix.addGuardrail)}>
                          Add "{quickFix.addGuardrail}" guardrail
                        </Button>
                      )}
                      {quickFix.inputType === 'number' && (
                        <div className="flex items-center gap-1.5">
                          <input type="number" id={qfInputId} defaultValue={quickFix.defaultValue}
                            className="v2-focus-ring w-[100px] bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-2 py-1 text-[11.5px]" />
                          <span className="text-[10.5px] text-v2-ink-muted">ms</span>
                          <Button variant="secondary" size="sm" onClick={() => {
                            const el = document.getElementById(qfInputId);
                            const v = parseInt(el?.value, 10);
                            if (Number.isFinite(v) && v > 0) handleQuickFix(quickFix.field, v);
                          }}>Apply</Button>
                        </div>
                      )}
                      {quickFix.inputType === 'text' && (
                        <div className="flex items-center gap-1.5">
                          <input type="text" id={qfInputId} placeholder="Enter skill description…"
                            defaultValue={typeof quickFix.current === 'string' && !quickFix.current.startsWith('(') ? quickFix.current : ''}
                            className="v2-focus-ring flex-1 bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-2 py-1 text-[11.5px]" />
                          <Button variant="secondary" size="sm" onClick={() => {
                            const el = document.getElementById(qfInputId);
                            const v = el?.value?.trim();
                            if (v) handleQuickFix(quickFix.field, v);
                          }}>Save</Button>
                        </div>
                      )}
                    </div>
                  )}

                  {status === 'passed' && (actual.reason || actual.evaluatedField) && (
                    <div className="mt-1 text-[11px] text-v2-ink-muted">
                      {actual.reason || 'Test passed'}
                      {actual.evaluatedField && <span className="ml-1.5">({actual.evaluatedField})</span>}
                    </div>
                  )}
                  {(tc.input || tc.expectedOutput || tc.actualOutput) && (
                    <details className="mt-1.5">
                      <summary className="text-[10.5px] text-v2-ink-muted cursor-pointer">Show test I/O</summary>
                      <div className="mt-1 space-y-1 text-[10.5px] font-mono-v2 text-v2-ink-secondary">
                        {tc.input && <div><strong>Input:</strong> {JSON.stringify(tc.input)}</div>}
                        {tc.expectedOutput && <div><strong>Expected:</strong> {JSON.stringify(tc.expectedOutput)}</div>}
                        {tc.actualOutput && <div><strong>Actual:</strong> {JSON.stringify(tc.actualOutput)}</div>}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* AI recommendations */}
      {recItems.length > 0 && (
        <details className="rounded-v2-md border border-v2-border-subtle bg-v2-surface" open>
          <summary className="cursor-pointer px-3 py-2 text-[13px] font-semibold text-v2-ink flex items-center gap-2">
            <SparklesIcon size={14} /> AI recommendations ({recItems.length})
          </summary>
          <div className="px-3 pb-3 space-y-2">
            {recItems.map((rec, i) => (
              <div key={i} className="rounded-v2-sm border border-v2-border-subtle bg-v2-muted px-3 py-2">
                <div className="flex items-center gap-2 mb-0.5">
                  <Pill tone={recTone(rec.priority)} size="sm">{rec.priority || 'info'}</Pill>
                  <span className="font-semibold text-v2-ink text-[12px]">{rec.title}</span>
                </div>
                {rec.detail && <div className="text-[11.5px] text-v2-ink-secondary leading-relaxed">{rec.detail}</div>}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* run history */}
      <div>
        <div className="text-[13px] font-semibold text-v2-ink mb-2 flex items-center gap-2">
          <RefreshCw size={14} /> Run history ({runs.length})
        </div>
        {runs.length === 0 ? (
          <div className="text-[11.5px] text-v2-ink-muted italic">No runs yet. Click Run eval to execute every test case.</div>
        ) : (
          <div className="space-y-1.5">
            {runs.map((run, idx) => {
              const rs = run.score == null ? null : (Number(run.score) <= 1 ? Number(run.score) : Number(run.score) / 100);
              return (
                <div key={run.id || idx} className="flex items-center gap-3 rounded-v2-md border border-v2-border-subtle bg-v2-surface px-3 py-2">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-[11.5px] ${
                    rs == null ? 'bg-v2-muted text-v2-ink-muted' : rs >= 0.9 ? 'bg-v2-success-soft text-v2-success' : rs >= 0.7 ? 'bg-v2-warning-soft text-v2-warning' : 'bg-v2-danger-soft text-v2-danger'}`}>
                    {rs != null ? Math.round(rs * 100) : '—'}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-v2-ink">Run #{runs.length - idx}</div>
                    <div className="text-[10.5px] text-v2-ink-muted">
                      {(run.passedTests ?? 0)}/{run.totalTests ?? 0} passed
                      {run.durationMs ? ` · ${run.durationMs}ms` : ''}
                      {run.completedAt ? ` · ${new Date(run.completedAt).toLocaleString()}` : ''}
                    </div>
                  </div>
                  <Pill tone={run.status === 'completed' ? 'success' : 'muted'} size="sm">{run.status || '—'}</Pill>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
